import { useState, useEffect } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Users, Eye, EyeOff, CheckCircle2, ShieldCheck, Zap } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";


export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);
  const { signIn, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const blockedUntil = localStorage.getItem("login_blocked_until");
    if (blockedUntil && new Date().getTime() < parseInt(blockedUntil)) {
      setIsBlocked(true);
      const remainingTime = Math.ceil((parseInt(blockedUntil) - new Date().getTime()) / 60000);
      toast.error(`Acesso bloqueado por excesso de tentativas. Tente novamente em ${remainingTime} minutos.`);
    }
  }, []);

  if (user) {
    return <Navigate to="/redirect" replace />;
  }


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isBlocked) return toast.error("Acesso bloqueado temporariamente.");
    if (!email || !password) return toast.error("Preencha todos os campos");
    
    setLoading(true);

    try {
      const { data, error } = await signIn(email.trim().toLowerCase(), password);

      if (error) {
        setLoading(false);
        const newAttempts = loginAttempts + 1;
        setLoginAttempts(newAttempts);
        
        if (newAttempts >= 5) {
          const blockTime = new Date().getTime() + 15 * 60 * 1000; // 15 minutes
          localStorage.setItem("login_blocked_until", blockTime.toString());
          setIsBlocked(true);
          toast.error("Muitas tentativas falhas. Acesso bloqueado por 15 minutos.");
        } else {
          toast.error(`Email ou senha inválidos. Tentativa ${newAttempts} de 5.`);
        }
      } else if (data.user) {
        localStorage.removeItem("login_blocked_until");
        toast.success("Login realizado com sucesso! Redirecionando...");
        navigate("/redirect");
      }
    } catch (err: any) {
      setLoading(false);
      console.error("Login exception:", err);
      toast.error("Ocorreu um erro inesperado ao tentar entrar. Tente novamente.");
    }
  };


  return (
    <div className="flex min-h-screen bg-slate-50/50">
      {/* Lado Esquerdo - Branding & Visuals (Oculto no Mobile) */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden bg-white border-r border-slate-100">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-blue-50/80 via-transparent to-transparent opacity-70" />
        
        <div className="relative z-10 w-full flex flex-col justify-between p-8 md:p-16">
          <div className="flex items-center gap-2.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 shadow-lg shadow-blue-200">
              <Users className="h-6 w-6 text-white" />
            </div>
            <span className="text-2xl font-bold tracking-tight text-slate-900">RH Simples</span>
          </div>

          <div className="max-w-xl">
            <h1 className="text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.1]">
              Organize sua equipe sem <span className="text-blue-600">planilhas e papelada.</span>
            </h1>
            <p className="mt-6 text-xl text-slate-600 font-medium">
              Funcionários, documentos e férias em um só lugar. A gestão de RH que a sua empresa merece.
            </p>

            <div className="mt-12 grid grid-cols-1 gap-4">
              <div className="flex items-center gap-3 bg-white/50 backdrop-blur-sm p-4 rounded-2xl border border-slate-100 w-fit pr-8 hover:translate-x-1 transition-transform cursor-default shadow-sm">
                <div className="h-10 w-10 rounded-full bg-emerald-50 flex items-center justify-center">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Fácil de usar</p>
                  <p className="text-sm text-slate-500">Interface intuitiva para qualquer pessoa</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 bg-white/50 backdrop-blur-sm p-4 rounded-2xl border border-slate-100 w-fit pr-8 hover:translate-x-1 transition-transform cursor-default shadow-sm">
                <div className="h-10 w-10 rounded-full bg-blue-50 flex items-center justify-center">
                  <Zap className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">100% Online</p>
                  <p className="text-sm text-slate-500">Acesse de onde quiser, quando quiser</p>
                </div>
              </div>

              <div className="flex items-center gap-3 bg-white/50 backdrop-blur-sm p-4 rounded-2xl border border-slate-100 w-fit pr-8 hover:translate-x-1 transition-transform cursor-default shadow-sm">
                <div className="h-10 w-10 rounded-full bg-indigo-50 flex items-center justify-center">
                  <ShieldCheck className="h-5 w-5 text-indigo-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Segurança Total</p>
                  <p className="text-sm text-slate-500">Seus dados protegidos com criptografia</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-auto">
            <div className="flex items-center gap-2">
              <div className="flex -space-x-2">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-8 w-8 rounded-full border-2 border-white bg-slate-200" />
                ))}
              </div>
              <p className="text-sm text-slate-500">
                <span className="font-semibold text-slate-900">+500 empresas</span> já simplificaram o RH
              </p>
            </div>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-64 h-96 bg-blue-100/30 blur-3xl rounded-full" />
        <div className="absolute left-1/4 bottom-0 w-48 h-48 bg-indigo-100/20 blur-3xl rounded-full" />
      </div>

      {/* Lado Direito - Form */}
      <div className="flex w-full lg:w-[45%] items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-[420px] space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
          <div className="lg:hidden flex flex-col items-center gap-4 mb-8">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-600 shadow-xl shadow-blue-200">
              <Users className="h-7 w-7 text-white" />
            </div>
            <span className="text-2xl font-bold tracking-tight text-slate-900">RH Simples</span>
          </div>

          <div className="space-y-2 text-center lg:text-left">
            <h2 className="text-3xl font-bold tracking-tight text-slate-900">Bem-vindo de volta</h2>
            <p className="text-slate-500">Acesse sua conta para gerenciar sua equipe</p>
          </div>

          <div className="bg-white p-8 rounded-[2rem] border border-slate-100 shadow-2xl shadow-slate-200/50">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-semibold text-slate-700 ml-1">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="exemplo@empresa.com"
                  className="h-12 px-4 rounded-xl border-slate-200 focus:border-blue-500 focus:ring-blue-500/10 transition-all bg-slate-50/30"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between ml-1">
                  <Label htmlFor="password" title="Senha" className="text-sm font-semibold text-slate-700">Senha</Label>
                  <Link to="/recovery" className="text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors">
                    Esqueceu a senha?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Sua senha secreta"
                    className="h-12 px-4 rounded-xl border-slate-200 focus:border-blue-500 focus:ring-blue-500/10 transition-all bg-slate-50/30"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-1"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

            <Button 
                type="submit" 
                className="w-full h-12 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-base shadow-lg shadow-blue-200/50 transition-all active:scale-[0.98] disabled:opacity-70" 
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    <span>Entrando...</span>
                  </div>
                ) : (
                  "Entrar no Sistema"
                )}
              </Button>
            </form>
          </div>

          <p className="text-center text-sm font-medium text-slate-500">
            Ainda não tem conta?{" "}
            <Link to="/cadastro" className="text-blue-600 hover:text-blue-700 font-bold hover:underline underline-offset-4">
              Começar grátis
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
